import { useState, useEffect } from 'react';
import { Save, Eye, RotateCcw, AlertCircle } from 'lucide-react';

interface CustomTemplate {
  subject?: string;
  htmlBody?: string;
  textBody?: string;
  sections?: {
    greeting?: string;
    intro?: string;
    callToAction?: string;
    footer?: string;
    arrivalInstructions?: string;
    contactDetails?: string;
    [key: string]: string | undefined;
  };
  lastModified?: string;
  modifiedBy?: string;
}

interface EmailTemplateSchema {
  type: string;
  name: string;
  description: string;
  variables: Array<any>;
  defaultSubject: string;
  defaultHtml: string;
  defaultText: string;
  sampleData: Record<string, any>;
}

interface SectionEditorProps {
  schema?: EmailTemplateSchema;
  customTemplate: CustomTemplate | null;
  onSave: (template: CustomTemplate | null) => void;
  onPreview: (template?: CustomTemplate) => void;
  onReset: () => void;
  isSaving: boolean;
  isPreviewing: boolean;
}

export function SectionEditor({
  schema,
  customTemplate,
  onSave,
  onPreview,
  onReset,
  isSaving,
  isPreviewing,
}: SectionEditorProps) {
  const [subject, setSubject] = useState('');
  const [greeting, setGreeting] = useState('');
  const [intro, setIntro] = useState('');
  const [callToAction, setCallToAction] = useState('');
  const [footer, setFooter] = useState('');
  const [arrivalInstructions, setArrivalInstructions] = useState('');
  const [contactDetails, setContactDetails] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Load custom template data when it changes
  useEffect(() => {
    if (customTemplate) {
      setSubject(customTemplate.subject || '');
      setGreeting(customTemplate.sections?.greeting || '');
      setIntro(customTemplate.sections?.intro || '');
      setCallToAction(customTemplate.sections?.callToAction || '');
      setFooter(customTemplate.sections?.footer || '');
      setArrivalInstructions(customTemplate.sections?.arrivalInstructions || '');
      setContactDetails(customTemplate.sections?.contactDetails || '');
    } else {
      // Reset to empty (will use defaults)
      setSubject('');
      setGreeting('');
      setIntro('');
      setCallToAction('');
      setFooter('');
      setArrivalInstructions('');
      setContactDetails('');
    }
    setHasChanges(false);
  }, [customTemplate, schema?.type]);

  const handleFieldChange = (field: string, value: string) => {
    setHasChanges(true);
    switch (field) {
      case 'subject':
        setSubject(value);
        break;
      case 'greeting':
        setGreeting(value);
        break;
      case 'intro':
        setIntro(value);
        break;
      case 'callToAction':
        setCallToAction(value);
        break;
      case 'footer':
        setFooter(value);
        break;
      case 'arrivalInstructions':
        setArrivalInstructions(value);
        break;
      case 'contactDetails':
        setContactDetails(value);
        break;
    }
  };

  const handleSave = () => {
    // Only save if there are actual customizations
    const hasCustomizations = subject || greeting || intro || callToAction || footer || arrivalInstructions || contactDetails;

    if (!hasCustomizations) {
      // If all fields are empty, save null to remove customization
      onSave(null);
    } else {
      const template: CustomTemplate = {
        subject: subject || undefined,
        sections: {
          greeting: greeting || undefined,
          intro: intro || undefined,
          callToAction: callToAction || undefined,
          footer: footer || undefined,
          arrivalInstructions: arrivalInstructions || undefined,
          contactDetails: contactDetails || undefined,
        },
      };
      onSave(template);
    }
    setHasChanges(false);
  };

  const handlePreview = () => {
    const template: CustomTemplate = {
      subject: subject || undefined,
      sections: {
        greeting: greeting || undefined,
        intro: intro || undefined,
        callToAction: callToAction || undefined,
        footer: footer || undefined,
        arrivalInstructions: arrivalInstructions || undefined,
        contactDetails: contactDetails || undefined,
      },
    };
    onPreview(template);
  };

  if (!schema) {
    return (
      <div className="text-center text-muted-foreground py-8">
        Select a template to edit
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-info-muted border border-info-border text-info-foreground rounded-lg p-4 flex items-start gap-2">
        <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-medium mb-1">Section Editor Mode</p>
          <p>
            Customize specific sections of the email while keeping the default structure. Leave fields empty to use
            the default values. Use <code className="bg-info-muted/50 px-1 py-0.5 rounded">{'{{variableName}}'}</code> syntax
            for dynamic content.
          </p>
        </div>
      </div>

      {/* Subject Line */}
      <div className="space-y-2">
        <label className="block text-sm font-medium">
          Subject Line
          <span className="text-muted-foreground font-normal ml-2">
            (Default: <code className="text-xs">{schema.defaultSubject}</code>)
          </span>
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => handleFieldChange('subject', e.target.value)}
          placeholder={schema.defaultSubject}
          className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
        />
        <p className="text-xs text-muted-foreground">
          Leave empty to use the default subject line
        </p>
      </div>

      {/* Greeting Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium">
          Greeting
          <span className="text-muted-foreground font-normal ml-2">
            (Default: Hi {'{{toName}}'}, )
          </span>
        </label>
        <textarea
          value={greeting}
          onChange={(e) => handleFieldChange('greeting', e.target.value)}
          placeholder="Hi {{toName}},"
          rows={2}
          className="w-full px-3 py-2 border rounded-md font-mono text-sm bg-background text-foreground"
        />
        <p className="text-xs text-muted-foreground">
          The opening greeting of the email
        </p>
      </div>

      {/* Intro Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium">
          Introduction
          <span className="text-muted-foreground font-normal ml-2">
            (Default: Please see the details below:)
          </span>
        </label>
        <textarea
          value={intro}
          onChange={(e) => handleFieldChange('intro', e.target.value)}
          placeholder="Please see the details below:"
          rows={4}
          className="w-full px-3 py-2 border rounded-md font-mono text-sm bg-background text-foreground"
        />
        <p className="text-xs text-muted-foreground">
          The main introduction paragraph explaining the purpose of the email
        </p>
      </div>

      {/* Call to Action Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium">
          Call to Action
          <span className="text-muted-foreground font-normal ml-2">
            (Default: Click the button above to complete the details. It only takes a few minutes!)
          </span>
        </label>
        <textarea
          value={callToAction}
          onChange={(e) => handleFieldChange('callToAction', e.target.value)}
          placeholder="Click the button above to complete the details. It only takes a few minutes!"
          rows={3}
          className="w-full px-3 py-2 border rounded-md font-mono text-sm bg-background text-foreground"
        />
        <p className="text-xs text-muted-foreground">
          Text displayed under the action button, encouraging users to complete the task
        </p>
      </div>

      {/* Footer Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium">
          Footer
          <span className="text-muted-foreground font-normal ml-2">
            (Default: This is an automated message from LittleBooker.)
          </span>
        </label>
        <textarea
          value={footer}
          onChange={(e) => handleFieldChange('footer', e.target.value)}
          placeholder="This is an automated message from LittleBooker."
          rows={2}
          className="w-full px-3 py-2 border rounded-md font-mono text-sm bg-background text-foreground"
        />
        <p className="text-xs text-muted-foreground">
          Footer text displayed at the bottom of the email
        </p>
      </div>

      {/* Happening Reminder Specific Sections */}
      {schema.type === 'happeningReminder' && (
        <>
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold mb-4 text-primary">
              Happening Reminder Specific Sections
            </h3>
          </div>

          {/* Arrival Instructions Section */}
          <div className="space-y-2">
            <label className="block text-sm font-medium">
              Arrival Instructions
              <span className="text-muted-foreground font-normal ml-2">
                (Default: Please arrive 15 minutes before the scheduled start time.)
              </span>
            </label>
            <textarea
              value={arrivalInstructions}
              onChange={(e) => handleFieldChange('arrivalInstructions', e.target.value)}
              placeholder="Please arrive 15 minutes before the scheduled start time."
              rows={3}
              className="w-full px-3 py-2 border rounded-md font-mono text-sm bg-background text-foreground"
            />
            <p className="text-xs text-muted-foreground">
              Instructions for when performers should arrive. Use {'{{arrivalInfo}}'} for dynamic arrival time.
            </p>
          </div>

          {/* Contact Details Section */}
          <div className="space-y-2">
            <label className="block text-sm font-medium">
              Contact Details
              <span className="text-muted-foreground font-normal ml-2">
                (Default: If you have any questions, please reply to this email.)
              </span>
            </label>
            <textarea
              value={contactDetails}
              onChange={(e) => handleFieldChange('contactDetails', e.target.value)}
              placeholder="If you have any questions, please reply to this email."
              rows={3}
              className="w-full px-3 py-2 border rounded-md font-mono text-sm bg-background text-foreground"
            />
            <p className="text-xs text-muted-foreground">
              Contact information for recipients who have questions about the show
            </p>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-4 border-t">
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="h-4 w-4" />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>

        <button
          onClick={handlePreview}
          disabled={isPreviewing}
          className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent"
        >
          <Eye className="h-4 w-4" />
          {isPreviewing ? 'Loading...' : 'Preview'}
        </button>

        {customTemplate && (
          <button
            onClick={onReset}
            className="flex items-center gap-2 px-4 py-2 border border-error-border text-error rounded-md hover:bg-error-muted"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Default
          </button>
        )}

        {hasChanges && (
          <span className="text-sm text-muted-foreground ml-auto">
            Unsaved changes
          </span>
        )}
      </div>
    </div>
  );
}
